import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  RotateCcw,
  Settings,
  HelpCircle,
  Info,
  PictureInPicture,
  Gauge
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { LikeButton } from '../like-button';
import type { StreamStatus } from '@/lib/shared-types/constants';

interface StreamControlsProps {
  isPlaying: boolean;
  isMuted: boolean;
  volume: number;
  streamStatus: StreamStatus;
  qualityLevels: Array<{ height: number; bitrate: number; label: string }>;
  currentQuality: number;
  isMobile: boolean;
  onPlayPause: () => void;
  onMuteToggle: () => void;
  onVolumeChange: (value: number[]) => void;
  onRestart: () => void;
  onQualityChange: (levelIndex: number) => void;
  onShowInfo: () => void;
  onShowShortcuts: () => void;
  streamId: number;
  likesCount: number;
  userId: string | number;
  onTogglePip?: () => void;
  isPipActive?: boolean;
  playbackSpeed?: number;
  onSpeedChange?: (speed: number) => void;
}

export function StreamControls({
  isPlaying,
  isMuted,
  volume,
  streamStatus,
  qualityLevels,
  currentQuality,
  isMobile,
  onPlayPause,
  onMuteToggle,
  onVolumeChange,
  onRestart,
  onQualityChange,
  onShowInfo,
  onShowShortcuts,
  streamId,
  likesCount,
  userId,
  onTogglePip,
  isPipActive = false,
  playbackSpeed = 1,
  onSpeedChange
}: StreamControlsProps) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-center flex-wrap gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={onPlayPause}
          className="text-white hover:bg-white/20"
          aria-label={isPlaying ? 'Pause video' : 'Play video'}
        >
          {isPlaying ? (
            <Pause className="h-5 w-5" />
          ) : (
            <Play className="h-5 w-5" />
          )}
        </Button>

        {streamStatus !== 'live' && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onRestart}
            className="text-white hover:bg-white/20"
            aria-label="Restart video"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
        )}

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={onMuteToggle}
            className="text-white hover:bg-white/20"
            aria-label={isMuted ? 'Unmute video' : 'Mute video'}
          >
            {isMuted ? (
              <VolumeX className="h-4 w-4" />
            ) : (
              <Volume2 className="h-4 w-4" />
            )}
          </Button>
          <div className="w-16 sm:w-20">
            <Slider
              value={[isMuted ? 0 : volume]}
              max={1}
              step={0.1}
              onValueChange={onVolumeChange}
              className="cursor-pointer"
            />
          </div>
        </div>

        {/* Picture-in-Picture Button */}
        {onTogglePip && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onTogglePip}
            className="text-white hover:bg-white/20"
            aria-label={isPipActive ? 'Exit picture-in-picture' : 'Enter picture-in-picture'}
            title={isPipActive ? 'Exit PiP' : 'Picture-in-Picture'}
          >
            <PictureInPicture className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="flex items-center flex-wrap gap-2">
        {isMobile && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onShowInfo}
            className="text-white hover:bg-white/20"
            title="Stream info"
          >
            <Info className="h-4 w-4" />
          </Button>
        )}

        {/* Like Button */}
        <LikeButton
          streamId={streamId.toString()}
          userId={userId}
          initialLikes={likesCount}
          variant="ghost"
          size="sm"
          className="text-white hover:bg-white/20"
        />

        <Button
          variant="ghost"
          size="icon"
          onClick={onShowShortcuts}
          className="text-white hover:bg-white/20"
          title="Keyboard shortcuts"
        >
          <HelpCircle className="h-4 w-4" />
        </Button>

        {/* Quality Selector */}
        {qualityLevels.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20"
              >
                <Settings className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Quality</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onQualityChange(-1)}
                className={currentQuality === -1 ? 'bg-accent' : ''}
              >
                Auto
              </DropdownMenuItem>
              {qualityLevels.map((level, index) => (
                <DropdownMenuItem
                  key={index}
                  onClick={() => onQualityChange(index)}
                  className={currentQuality === index ? 'bg-accent' : ''}
                >
                  {level.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Playback Speed Control */}
        {onSpeedChange && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20"
                title={`Playback speed: ${playbackSpeed}x`}
              >
                <Gauge className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Playback Speed</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {[1, 1.25, 1.5, 1.75, 2].map((speed) => (
                <DropdownMenuItem
                  key={speed}
                  onClick={() => onSpeedChange(speed)}
                  className={Math.abs(playbackSpeed - speed) < 0.01 ? 'bg-accent' : ''}
                >
                  {speed}x
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {!isMobile && qualityLevels.length === 0 && (
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/20"
            disabled
            title="Quality selection not available"
          >
            <Settings className="h-4 w-4 opacity-50" />
          </Button>
        )}
      </div>
    </div>
  );
}